import { useState } from "react";
import { useField, useFormikContext } from "formik";
import { Box, Card, Flex, Text, Grid, Tabs, Button } from "@radix-ui/themes";
import { WorkflowAction } from "@/types/breeze/Workflow";
import { IntegrationDefinition } from "@/types/breeze/Integration";
import { isInternalHostname } from "@/utils/hostname";
import { useAuth } from "@/providers/AuthProvider";
import IntegrationSelector from "./IntegrationSelector";
import {
  DatabaseIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  GlobeIcon,
  CodeIcon,
  TerminalIcon,
  GitBranchIcon,
  MailIcon,
  FileIcon,
  MergeIcon,
  BellIcon,
  CoinsIcon,
  ArrowLeftIcon,
  LucideIcon,
} from "lucide-react";

interface ActionOption {
  value: WorkflowAction;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
}

const actionOptions: ActionOption[] = [
  {
    value: WorkflowAction.ReadData,
    label: "Read Data",
    description: "Fetch and retrieve data",
    icon: DatabaseIcon,
    color: "blue",
  },
  {
    value: WorkflowAction.CreateData,
    label: "Create Data",
    description: "Add new records",
    icon: PlusIcon,
    color: "green",
  },
  {
    value: WorkflowAction.UpdateData,
    label: "Update Data",
    description: "Modify existing records",
    icon: EditIcon,
    color: "orange",
  },
  {
    value: WorkflowAction.DeleteData,
    label: "Delete Data",
    description: "Remove records",
    icon: TrashIcon,
    color: "red",
  },
  {
    value: WorkflowAction.CallAnApi,
    label: "Call An API",
    description: "Make HTTP requests",
    icon: GlobeIcon,
    color: "purple",
  },
  {
    value: WorkflowAction.RunScript,
    label: "Run Script",
    description: "Execute custom code",
    icon: CodeIcon,
    color: "cyan",
  },
  {
    value: WorkflowAction.SendACommand,
    label: "Send a Command",
    description: "Execute system commands",
    icon: TerminalIcon,
    color: "gray",
  },
  {
    value: WorkflowAction.Condition,
    label: "Condition",
    description: "Evaluate conditions and branch",
    icon: GitBranchIcon,
    color: "amber",
  },
  {
    value: WorkflowAction.SendAnEmail,
    label: "Send an Email",
    description: "Send templated email",
    icon: MailIcon,
    color: "blue",
  },
  {
    value: WorkflowAction.FileHandler,
    label: "File Handler",
    description: "Fetch, link, or detach a file on a record",
    icon: FileIcon,
    color: "indigo",
  },
  {
    value: WorkflowAction.UpsertData,
    label: "Upsert Data",
    description: "Match-or-create records by key field(s)",
    icon: MergeIcon,
    color: "teal",
  },
  {
    value: WorkflowAction.SendPushNotification,
    label: "Send a Push Notification",
    description: "Push a notification to a user, list, or broadcast",
    icon: BellIcon,
    color: "pink",
  },
  {
    value: WorkflowAction.AdjustBalance,
    label: "Adjust a Balance",
    description: "Add or take points or credit from a user's balance",
    icon: CoinsIcon,
    color: "gold",
  },
];

interface ActionSelectorProps {
  name: string;
  label?: string;
  required?: boolean;
  onChange?: (value: WorkflowAction) => void;
  onBrowsingChange?: (isBrowsing: boolean) => void;
}

export default function ActionSelector({
  name,
  label,
  required = false,
  onChange,
  onBrowsingChange,
}: ActionSelectorProps) {
  const [field, meta, helpers] = useField(name);
  const { setFieldValue, values } = useFormikContext<any>();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || 0;

  const isIntegration = field.value === WorkflowAction.Integration;
  const hasAction = field.value !== WorkflowAction.None;
  const [activeTab, setActiveTab] = useState(
    isIntegration ? "integrations" : "actions",
  );
  const [browsingActions, setBrowsingActionsState] = useState(
    !hasAction || isIntegration,
  );

  const setBrowsingActions = (val: boolean) => {
    setBrowsingActionsState(val);
    onBrowsingChange?.(val);
  };

  const handleActionSelect = (action: WorkflowAction) => {
    helpers.setValue(action);
    setBrowsingActions(false);
    // Clear integration-specific parameters when switching to a regular action
    if (action !== WorkflowAction.Integration) {
      setFieldValue("parameters.provider", undefined);
      setFieldValue("parameters.operation", undefined);
      setFieldValue("parameters.credential_source", undefined);
      setFieldValue("parameters.inputs", undefined);
    }
    onChange?.(action);
  };

  const handleIntegrationSelect = (definition: IntegrationDefinition) => {
    helpers.setValue(WorkflowAction.Integration);
    setFieldValue("parameters.provider", definition.provider);
    setFieldValue("parameters.operation", "");
    setFieldValue("parameters.credential_source", "system");
    setFieldValue("parameters.inputs", {});
    onChange?.(WorkflowAction.Integration);
  };

  const handleChangeAction = () => {
    setBrowsingActions(true);
  };

  const isSelected = (action: WorkflowAction) => field.value === action;

  // Filter out SendACommand if hostname is not allowed
  const availableActions = actionOptions.filter((option) => {
    if (option.value === WorkflowAction.SendACommand) {
      return isInternalHostname();
    }
    return true;
  });

  const selectedProvider = isIntegration
    ? (values?.parameters?.provider as string)
    : undefined;

  // Find the selected action option for the collapsed view
  const selectedActionOption = actionOptions.find(
    (o) => o.value === field.value,
  );

  // Collapsed action view — show selected action compactly
  if (hasAction && !isIntegration && !browsingActions && selectedActionOption) {
    const Icon = selectedActionOption.icon;
    return (
      <Box>
        {label && (
          <Text size="2" weight="medium" mb="2" as="label">
            {label}
            {required && (
              <Text color="red" ml="1">
                *
              </Text>
            )}
          </Text>
        )}
        <Card
          style={{
            border: `2px solid var(--${selectedActionOption.color}-9)`,
            backgroundColor: `var(--${selectedActionOption.color}-2)`,
          }}
        >
          <Flex justify="between" align="center" p="3">
            <Flex gap="3" align="center">
              <Box
                style={{
                  padding: "6px",
                  borderRadius: 8,
                  backgroundColor: `var(--${selectedActionOption.color}-9)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={18} color="white" />
              </Box>
              <Box>
                <Text size="2" weight="bold">
                  {selectedActionOption.label}
                </Text>
                <Text size="1" color="gray" style={{ display: "block" }}>
                  {selectedActionOption.description}
                </Text>
              </Box>
            </Flex>
            <Button variant="soft" size="1" onClick={handleChangeAction}>
              <ArrowLeftIcon size={12} /> Change
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  // Full picker view
  return (
    <Box>
      {label && (
        <Text size="2" weight="medium" mb="2" as="label">
          {label}
          {required && (
            <Text color="red" ml="1">
              *
            </Text>
          )}
        </Text>
      )}

      <Tabs.Root
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab);
          // Switching tabs means we're browsing
          setBrowsingActions(true);
        }}
      >
        <Tabs.List>
          <Tabs.Trigger value="actions">Actions</Tabs.Trigger>
          <Tabs.Trigger value="integrations">Integrations</Tabs.Trigger>
        </Tabs.List>

        <Box pt="3">
          <Tabs.Content value="actions">
            <Grid columns="3" gap="3" width="100%">
              {availableActions.map((option) => {
                const Icon = option.icon;
                const selected = isSelected(option.value);

                return (
                  <Card
                    key={option.value}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: selected
                        ? `2px solid var(--${option.color}-9)`
                        : "1px solid var(--gray-6)",
                      backgroundColor: selected
                        ? `var(--${option.color}-4)`
                        : "var(--color-panel-solid)",
                      transform: selected ? "scale(1.02)" : "scale(1)",
                    }}
                    onClick={() => handleActionSelect(option.value)}
                    onMouseEnter={(e) => {
                      if (!selected) {
                        e.currentTarget.style.backgroundColor = "var(--gray-3)";
                        e.currentTarget.style.transform = "scale(1.01)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) {
                        e.currentTarget.style.backgroundColor =
                          "var(--color-panel-solid)";
                        e.currentTarget.style.transform = "scale(1)";
                      }
                    }}
                  >
                    <div>
                      <Box p="3">
                        <Flex direction="column" align="center" gap="2">
                          <Box
                            style={{
                              padding: "8px",
                              borderRadius: "8px",
                              backgroundColor: selected
                                ? `var(--${option.color}-9)`
                                : `var(--${option.color}-4)`,
                            }}
                          >
                            <Icon
                              size={20}
                              color={
                                selected ? "white" : `var(--${option.color}-11)`
                              }
                            />
                          </Box>
                          <Box style={{ textAlign: "center" }}>
                            <Text
                              size="2"
                              weight="medium"
                              style={{
                                color: selected
                                  ? `var(--${option.color}-11)`
                                  : "var(--gray-12)",
                              }}
                            >
                              {option.label}
                            </Text>
                            <Text
                              size="1"
                              style={{
                                color: selected
                                  ? `var(--${option.color}-10)`
                                  : "var(--gray-10)",
                                display: "block",
                                marginTop: "2px",
                              }}
                            >
                              {option.description}
                            </Text>
                          </Box>
                        </Flex>
                      </Box>
                    </div>
                  </Card>
                );
              })}
            </Grid>
          </Tabs.Content>

          <Tabs.Content value="integrations">
            <IntegrationSelector
              tenantId={tenantId}
              onSelect={handleIntegrationSelect}
              selectedProvider={selectedProvider}
              onBrowsingChange={onBrowsingChange}
            />
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      {meta.touched && meta.error && (
        <Text size="1" color="red" mt="1">
          {meta.error}
        </Text>
      )}
    </Box>
  );
}
